import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles(() => ({
  root: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: 7,
    alignItems: 'center',
  },
  item: {
    position: 'relative',
    display: 'block',
    cursor: 'default',
  },
  icon: {
    display: 'block',
    borderRadius: '50%',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.12)',
  },
  blank: {
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    fontSize: '0.7rem',
    fontWeight: 800,
    color: 'rgba(255, 255, 255, 0.6)',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  count: {
    position: 'absolute',
    right: -6,
    bottom: -4,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    boxSizing: 'border-box',
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    fontSize: '0.66rem',
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    color: '#fff',
    background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
    border: '1.5px solid #15161a',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
  },
  tip: {
    display: 'grid',
    gridGap: 2,
    padding: '2px 0',
    fontSize: '0.78rem',
  },
  tipTitle: {
    fontWeight: 800,
    marginBottom: 2,
  },
  tipMuted: {
    opacity: 0.65,
  },
}));

interface ClassPlayer {
  name?: string | null;
  class?: string | null;
  role?: string | null;
  adHoc?: boolean | null;
}

interface Props {
  players: ClassPlayer[];
  /** icon size in px */
  size?: number;
}

/**
 * A group's make-up: one icon per class with how many of it there are; hovering shows who they are. Takes far less
 * room than an icon per character, which mattered for raids.
 */
export const ClassCounts: React.FC<Props> = ({players, size = 26}) => {
  const classes = useStyles();
  const byClass: Record<string, ClassPlayer[]> = {};
  players.forEach((p) => {
    const key = p.class || '';
    (byClass[key] = byClass[key] || []).push(p);
  });
  // most common first, then by name; unknown classes last
  const groups = Object.keys(byClass).sort((a, b) =>
    (a ? 0 : 1) - (b ? 0 : 1) || byClass[b].length - byClass[a].length || a.localeCompare(b));

  return (
    <span className={classes.root}>
      {groups.map((aocClass) => {
        const list = byClass[aocClass];
        const icon = aocClass ? classImages.getColorIcon(aocClass as AocClasses) : '';
        return (
          <Tooltip key={aocClass || 'unknown'} title={
            <div className={classes.tip}>
              <div className={classes.tipTitle}>{aocClass || 'Unknown class'} × {list.length}</div>
              {list.map((p, i) => (
                <div key={`${p.name}-${i}`}>
                  {p.name}{p.role ? <span className={classes.tipMuted}> ({p.role})</span> : null}{p.adHoc ? <span className={classes.tipMuted}> · ad-hoc</span> : null}
                </div>
              ))}
            </div>
          }>
            <span className={classes.item}>
              {icon
                ? <img className={classes.icon} src={icon} alt={aocClass} style={{width: size, height: size}} />
                : <span className={classes.blank} style={{width: size, height: size}}>?</span>}
              <span className={classes.count}>{list.length}</span>
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}
